import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Building2, GraduationCap, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { PublicHeader } from "@/components/Layout";
import { SkeletonInlineStats } from "@/components/PageSkeleton";

const HERO_BG = "https://customer-assets-wrfwihn1.emergentagent.net/job_diploma-check-4/artifacts/egksrsr6_WhatsApp%20Image%202026-08-26%20at%202.53.08%20AM.jpeg";

const DESCRIPTION =
  "CertiSeal lets employers, registrars, and verifiers instantly authenticate diplomas, certificates, and degrees issued by partner institutions — using a single ID and voucher code.";

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_institutions: 0, total_graduates: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      <PublicHeader transparent />

      <section className="relative min-h-[calc(100vh-4rem)] -mt-16 pt-16 flex items-center justify-center px-6 md:px-12 py-24">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          data-testid="home-hero-bg"
          aria-hidden="true"
        />
        {/* Contrast gradient — keeps text readable against the busy photo */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/50 to-slate-950/85" aria-hidden="true" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(2,6,23,0.6)_70%)]" aria-hidden="true" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-8" data-testid="home-badge">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-300" />
              <span className="text-xs font-semibold tracking-widest uppercase text-blue-100">Verified Credential Network</span>
            </div>

            <h1
              className="font-display font-black tracking-tight text-white leading-[0.9]"
              style={{ fontSize: "clamp(56px, 12vw, 168px)", textShadow: "0 4px 30px rgba(0,0,0,0.35)" }}
              data-testid="home-brand"
            >
              CertiSeal
            </h1>

            <div className="mt-5 text-xs md:text-sm font-semibold tracking-[0.35em] uppercase text-blue-200" data-testid="home-tagline">
              Your Trusted Academic Proof
            </div>

            <p className="mt-10 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-slate-200" data-testid="home-description">
              {DESCRIPTION}
            </p>

            {/* CTA */}
            <div className="mt-12 flex items-center justify-center gap-4 flex-wrap">
              <Button
                size="lg"
                onClick={() => navigate("/verify")}
                className="h-14 px-8 rounded-full bg-white text-slate-900 hover:bg-slate-100 font-semibold text-base shadow-2xl shadow-blue-500/20 group"
                data-testid="home-verify-cta"
              >
                <ShieldCheck className="w-5 h-5 mr-2 text-blue-600" />
                Verify Credential
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>

            {/* Stats */}
            <div className="mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              {loading ? (
                <SkeletonInlineStats count={2} />
              ) : (
                <>
                  <StatPill icon={Building2} value={stats.total_institutions} label={stats.total_institutions === 1 ? "institution" : "institutions"} testId="home-stat-institutions" />
                  <div className="hidden md:block w-px h-8 bg-white/20" aria-hidden="true" />
                  <StatPill icon={GraduationCap} value={stats.total_graduates} label={stats.total_graduates === 1 ? "verified graduate" : "verified graduates"} testId="home-stat-graduates" />
                </>
              )}
            </div>
          </motion.div>

          {/* Scroll hint */}
          <motion.div
            className="mt-24 text-white/60 text-xs uppercase tracking-widest"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0.4] }}
            transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
            aria-hidden="true"
          >
            <Link to="/registry" className="hover:text-white transition-colors">
              Browse partner institutions ↓
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function StatPill({ icon: Icon, value, label, testId }) {
  return (
    <div className="inline-flex items-center gap-3" data-testid={testId}>
      <Icon className="w-5 h-5 text-blue-300 flex-shrink-0" />
      <div className="text-left">
        <div className="font-display font-extrabold text-3xl md:text-4xl text-white leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-widest text-slate-300 mt-1">{label}</div>
      </div>
    </div>
  );
}
